/**
 * Topic browser (brief §content: `/library/topics`). Chapter cards, each
 * showing its Y&F pointer ("Y&F 39.1, 38.4, 39.6", explained once by the
 * legend line under the header) and expandable to its topic rows — counts by
 * question type, the flashcard count, the "kursiv läsning" and
 * "föreläsningsanteckningar" badges and an "Öva detta / Practice this" button
 * that preselects the topic on `/practice` (`?chapters=&topics=`, the same
 * convention `progress`'s "study next" and the exam result's "practice your
 * weakest chapters" links use). Topic rows carry no Y&F sections of their own.
 *
 * Slice B: a "Moment / Föreläsning" tab (`?view=lectures`, P1-2) switches to the lecture list
 * (`LecturesView.tsx`), and each topic row is an anchor (`#<topic id>`, P1-4): a link with a hash
 * opens that topic's module card and scrolls the row into view.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useLocalizedText } from "@client/components/Localized";
import { Badge, Button, Card, EmptyState, PageHeader, Spinner, Tabs } from "@client/components/ui";
import { useT, useTp } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { useContentOverview } from "./api";
import { formatBookRef } from "./bookRef";
import { LecturesView } from "./LecturesView";
import { practiceHref } from "./links";
import type { ChapterOverview, TopicOverview } from "./types";

/** Authored `examWeight` estimates run 0–1; they are shown as three bands cut at these values. */
export const EXAM_WEIGHT_BANDS = { mid: 0.35, high: 0.75 } as const;

export function weightKey(weight: number) {
  return weight >= EXAM_WEIGHT_BANDS.high
    ? ("content.topics.weight.high" as const)
    : weight >= EXAM_WEIGHT_BANDS.mid
      ? ("content.topics.weight.mid" as const)
      : ("content.topics.weight.low" as const);
}

function TopicRow({ topic }: { topic: TopicOverview }) {
  const t = useT();
  const localize = useLocalizedText();
  const navigate = useNavigate();
  const { counts } = topic;
  const bookRef = formatBookRef(topic.book, t);

  const typeParts = [
    counts.mc > 0 ? `${counts.mc} ${t("content.topics.type.mc")}` : null,
    counts.numeric > 0 ? `${counts.numeric} ${t("content.topics.type.numeric")}` : null,
    counts.open > 0 ? `${counts.open} ${t("content.topics.type.open")}` : null,
  ].filter((part): part is string => part !== null);

  return (
    <li
      id={topic.id}
      className="scroll-mt-24 flex flex-col gap-3 border-b border-ink-100 py-3 last:border-0 dark:border-ink-900 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-ink-800 dark:text-ink-100">{localize(topic.name)}</p>
          <span
            className="text-xs text-ink-600 dark:text-ink-400"
            title={t("content.topics.weight.hint")}
          >
            {t("content.topics.weight", { level: t(weightKey(topic.examWeight)) })}
          </span>
          {topic.cursory && <Badge tone="neutral">{t("content.topics.cursory")}</Badge>}
          {topic.book.lectureNotes && (
            <Badge tone="neutral">{t("content.topics.lectureNotes")}</Badge>
          )}
        </div>
        {bookRef !== null && (
          <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">{bookRef}</p>
        )}
        <p className="mt-1 text-sm text-ink-600 dark:text-ink-400">
          {counts.total > 0
            ? `${t("content.topics.counts.questions", { count: counts.total })}${typeParts.length > 0 ? ` (${typeParts.join(", ")})` : ""} · ${t("content.topics.counts.flashcards", { count: counts.flashcards })}`
            : t("content.topics.counts.empty")}
        </p>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate(practiceHref(topic.chapter, topic.id))}
        disabled={counts.total === 0}
      >
        {t("content.topics.practice")}
      </Button>
    </li>
  );
}

/** The topic id a `#hash` names, if any. */
function hashTopic(hash: string): string {
  try {
    return decodeURIComponent(hash.replace(/^#/, ""));
  } catch {
    return "";
  }
}

function ChapterCard({ chapter }: { chapter: ChapterOverview }) {
  const t = useT();
  const tp = useTp();
  const localize = useLocalizedText();
  const { hash } = useLocation();
  const target = hashTopic(hash);
  const holdsTarget = target !== "" && chapter.topics.some((topic) => topic.id === target);
  const [expanded, setExpanded] = useState(holdsTarget);
  // A chip link (`/library/topics#photoelectric-effect`) opens the card that holds the topic.
  useEffect(() => {
    if (!holdsTarget) return;
    setExpanded(true);
    const row = document.getElementById(target);
    row?.scrollIntoView?.({ block: "start" });
  }, [holdsTarget, target]);
  // "Y&F 37.1–37.9 · föreläsningsanteckningar · 11 ämnen" — no pointer when the book ref is
  // empty; "1 ämne" for a one-topic module (the plural pair `content.topics.count.one/.other`).
  const summary = [
    formatBookRef(chapter.book, t),
    tp("content.topics.count", chapter.topics.length),
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <Card compact className="mb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 text-left"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-ink-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-400" />
        )}
        <div className="flex-1">
          <p className="font-medium text-ink-800 dark:text-ink-100">
            {chapter.number}. {localize(chapter.title)}
          </p>
          <p className="text-sm text-ink-500 dark:text-ink-400">{summary}</p>
        </div>
      </button>

      {expanded && (
        <ul className="mt-4">
          {chapter.topics.map((topic) => (
            <TopicRow key={topic.id} topic={topic} />
          ))}
        </ul>
      )}
    </Card>
  );
}

type TopicsView = "modules" | "lectures";

export function TopicsPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const view: TopicsView = params.get("view") === "lectures" ? "lectures" : "modules";

  function changeView(next: string) {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next === "lectures") updated.set("view", "lectures");
        else updated.delete("view");
        return updated;
      },
      { replace: true },
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("content.nav.topics")} description={t("content.topics.description")} />
      <p className="mb-4 text-sm text-ink-600 dark:text-ink-400">{t("content.topics.legend")}</p>
      <Tabs
        ariaLabel={t("content.topics.view.label")}
        value={view}
        onChange={changeView}
        className="mb-4"
        items={[
          { id: "modules", label: t("content.topics.view.modules"), content: <ModulesView /> },
          { id: "lectures", label: t("content.topics.view.lectures"), content: <LecturesView /> },
        ]}
      />
    </div>
  );
}

function ModulesView() {
  const t = useT();
  const overviewQuery = useContentOverview();
  const chapters = overviewQuery.data?.chapters ?? [];

  return (
    <>
      {overviewQuery.isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" label={t("common.loading")} />
        </div>
      )}

      {overviewQuery.isError && <EmptyState title={messageForError(overviewQuery.error, t)} />}

      {overviewQuery.isSuccess && chapters.length === 0 && (
        <EmptyState title={t("content.topics.empty")} />
      )}

      {chapters.map((chapter) => (
        <ChapterCard key={chapter.number} chapter={chapter} />
      ))}
    </>
  );
}

export default TopicsPage;
